import React, { useState } from 'react';
import { sandboxFromManifest, type WidgetManifest } from '../../services/myToolsApi';
import WidgetFrame from './WidgetFrame';
import { previewManifest } from './widgetInstallControls';
import '../../styles/Widgets.css';

export interface WidgetPreviewProps {
  toolId: string;
  manifest: WidgetManifest | null | undefined;
  height?: number;
  title?: string;
  /** Reviewers may opt in to run the preview with the widget's requested permissions (using their own access). */
  allowPermissionsToggle?: boolean;
}

/** Look-before-you-install preview. Runs with NO permissions by default: nothing the viewer
 *  hasn't granted may be exercised by code they are only looking at. */
const WidgetPreview: React.FC<WidgetPreviewProps> = ({ toolId, manifest, height = 320, title, allowPermissionsToggle = false }) => {
  const [withPermissions, setWithPermissions] = useState(false);
  const requested = manifest?.permissions ?? [];
  const effective = withPermissions ? (manifest ?? null) : previewManifest(manifest);

  return (
    <div className="wg-preview">
      <div className="wg-preview__bar">
        <span className="wg-preview__note">
          {withPermissions
            ? `Preview is running with the widget's ${requested.length} requested permission${requested.length === 1 ? '' : 's'}, using your account.`
            : requested.length > 0
              ? `Preview runs without permissions — the widget asks for ${requested.length}; install it to grant them.`
              : 'Preview runs in an isolated sandbox.'}
        </span>
        {allowPermissionsToggle && requested.length > 0 && (
          <label className="wg-preview__toggle">
            <input type="checkbox" checked={withPermissions} onChange={e => setWithPermissions(e.target.checked)} />
            Run with requested permissions
          </label>
        )}
      </div>
      <WidgetFrame
        key={withPermissions ? 'with' : 'without'}
        toolId={toolId}
        manifest={effective}
        grantedSandbox={sandboxFromManifest(effective)}
        surface="page"
        height={height}
        title={title ?? `${manifest?.name ?? 'Widget'} preview`}
        // A look-before-you-install preview must not read or change the data the viewer's real
        // install of this widget keeps (Momentum.storage isn't permission-gated).
        ephemeralStorage
      />
    </div>
  );
};

export default WidgetPreview;
